/**
 * PurchaseEntryScreen — record a "Goods Received" purchase from a supplier.
 *
 * - Pick supplier (typeahead) → reactive `suppliers` query
 * - Pick godown (optional) → defaults to first/default godown
 * - Add product lines via search; edit qty / unit price / tax %
 * - Live totals (subtotal, tax, grand total)
 * - Submit → atomic write through PurchaseService
 *
 * On success we navigate back to /inventory and surface the new PO number
 * via the in-page success banner.
 */

import { notifySuccess } from '@/store/useToastStore';
import { useErrorPopup } from '@/hooks/useErrorPopup';
import { useEffect, useMemo, useState } from 'react';
import { Q } from '@nozbe/watermelondb';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Save } from 'lucide-react';
import { database } from '@/db';
import { Product, Supplier, Godown } from '@/db/models/Inventory';
import { useAppStore } from '@/store/useAppStore';
import { useObservable } from '@/hooks/useObservable';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { formatCurrency } from '@/utils/format';
import { createReceivedPurchase, type PurchaseLine } from '@/inventory/PurchaseService';

interface DraftLine {
  product: Product;
  quantity: string;
  unitPrice: string;
  taxRate: string;
}

function lineTotals(l: DraftLine) {
  const qty = parseFloat(l.quantity) || 0;
  const price = parseFloat(l.unitPrice) || 0;
  const tax = parseFloat(l.taxRate) || 0;
  const sub = qty * price;
  const taxAmt = sub * (tax / 100);
  return { sub, taxAmt, total: sub + taxAmt };
}

export function PurchaseEntryScreen() {
  const navigate = useNavigate();
  const activeStoreId = useAppStore((s) => s.activeStoreId);
  const merchantId = useAppStore((s) => s.currentUser?.merchantId);
  const userId = useAppStore((s) => s.currentUser?.id);

  // Suppliers
  const [supplierSearch, setSupplierSearch] = useState('');
  const [supplierDebounced, setSupplierDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setSupplierDebounced(supplierSearch.trim().toLowerCase()), 180);
    return () => clearTimeout(t);
  }, [supplierSearch]);

  const suppliers = useObservable<Supplier[]>(
    () => {
      if (!merchantId) return null;
      const conds: any[] = [Q.where('merchant_id', merchantId), Q.where('is_deleted', false)];
      if (supplierDebounced.length >= 1) {
        const q = `%${supplierDebounced}%`;
        conds.push(Q.or(Q.where('name', Q.like(q)), Q.where('phone', Q.like(q)), Q.where('gstin', Q.like(q))));
      }
      return database.get<Supplier>('suppliers').query(...conds, Q.take(15)).observe();
    },
    [merchantId, supplierDebounced],
    [],
  );
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);

  // Godowns
  const godowns = useObservable<Godown[]>(
    () => {
      if (!activeStoreId) return null;
      return database.get<Godown>('godowns')
        .query(Q.where('store_id', activeStoreId), Q.where('is_deleted', false))
        .observe();
    },
    [activeStoreId],
    [],
  );
  const [godownId, setGodownId] = useState<string>('');
  useEffect(() => {
    if (!godownId && godowns.length > 0) {
      const def = godowns.find((g) => g.isDefault) ?? godowns[0];
      setGodownId(def.id);
    }
  }, [godowns, godownId]);

  // Lines & product picker
  const [lines, setLines] = useState<DraftLine[]>([]);
  const [productSearch, setProductSearch] = useState('');
  const [productDebounced, setProductDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setProductDebounced(productSearch.trim().toLowerCase()), 180);
    return () => clearTimeout(t);
  }, [productSearch]);

  const productResults = useObservable<Product[]>(
    () => {
      if (!activeStoreId || productDebounced.length < 2) return null;
      const q = `%${productDebounced}%`;
      return database.get<Product>('products')
        .query(
          Q.where('store_id', activeStoreId),
          Q.where('is_deleted', false),
          Q.or(Q.where('name', Q.like(q)), Q.where('sku', Q.like(q)), Q.where('barcode', Q.like(q))),
          Q.take(15),
        )
        .observe();
    },
    [activeStoreId, productDebounced],
    [],
  );

  // Other fields
  const [supplierInvoiceNo, setSupplierInvoiceNo] = useState('');
  const [supplierInvoiceDate, setSupplierInvoiceDate] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  useErrorPopup(error, () => setError(null));
  const [saving, setSaving] = useState(false);

  const totals = useMemo(() => {
    let sub = 0, tax = 0;
    for (const l of lines) {
      const t = lineTotals(l);
      sub += t.sub;
      tax += t.taxAmt;
    }
    return { sub, tax, grand: sub + tax };
  }, [lines]);

  function addLine(p: Product) {
    setLines((prev) => [
      ...prev,
      {
        product: p,
        quantity: '1',
        unitPrice: String(p.purchasePrice || 0),
        taxRate: String(p.gstRate || 0),
      },
    ]);
    setProductSearch('');
  }

  function removeLine(i: number) {
    setLines((prev) => prev.filter((_, idx) => idx !== i));
  }

  function updateLine(i: number, patch: Partial<DraftLine>) {
    setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  }

  async function handleSubmit() {
    setError(null);
    if (!selectedSupplier) { setError('Pick a supplier.'); return; }
    if (!activeStoreId) { setError('No active store.'); return; }
    if (!userId) { setError('Not signed in.'); return; }
    if (lines.length === 0) { setError('Add at least one line.'); return; }

    const payload: PurchaseLine[] = [];
    for (const l of lines) {
      const qty = parseFloat(l.quantity);
      const price = parseFloat(l.unitPrice);
      const tax = parseFloat(l.taxRate) || 0;
      if (!Number.isFinite(qty) || qty <= 0) { setError(`Invalid qty for ${l.product.name}.`); return; }
      if (!Number.isFinite(price) || price < 0) { setError(`Invalid price for ${l.product.name}.`); return; }
      payload.push({ product: l.product, quantity: qty, unitPrice: price, taxRate: tax });
    }

    setSaving(true);
    const res = await createReceivedPurchase({
      supplier: selectedSupplier,
      storeId: activeStoreId,
      godownId: godownId || null,
      supplierInvoiceNo: supplierInvoiceNo.trim() || null,
      supplierInvoiceDate: supplierInvoiceDate ? new Date(supplierInvoiceDate) : null,
      notes: notes.trim() || null,
      lines: payload,
      createdById: userId,
    });
    setSaving(false);
    if (res.success) {
      notifySuccess(`Purchase ${res.poNumber ?? ''} saved — stock updated.`.replace('  ', ' '));
      navigate('/inventory', { state: { lastPoNumber: res.poNumber } });
    } else {
      setError(res.error ?? 'Failed to save purchase.');
    }
  }

  if (!activeStoreId || !merchantId) {
    return <div className="text-fg-secondary text-body">No active store. Sign in again.</div>;
  }

  return (
    <div className="flex h-full flex-col gap-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="rounded-md p-1.5 text-fg-tertiary hover:bg-bg-subtle hover:text-fg-primary"
            aria-label="Back"
          >
            <ArrowLeft size={16} />
          </button>
          <div>
            <h1 className="text-display text-fg-primary">New purchase</h1>
            <p className="mt-1 text-body text-fg-secondary">Record stock received from a supplier.</p>
          </div>
        </div>
        <Button onClick={handleSubmit} disabled={saving || lines.length === 0 || !selectedSupplier}>
          <Save size={14} /> {saving ? 'Saving…' : 'Save purchase'}
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-5 overflow-auto lg:grid-cols-[1fr_360px] lg:overflow-hidden">
        {/* LEFT: lines */}
        <div className="flex flex-col gap-4 overflow-hidden">
          {/* Product picker */}
          <div className="relative">
            <Input
              placeholder="Add product (search by name, SKU, barcode)…"
              value={productSearch}
              onChange={(e) => setProductSearch(e.target.value)}
            />
            {productResults.length > 0 && (
              <div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-72 overflow-auto rounded-md border border-stroke-hairline/20 bg-bg-surface shadow-lg">
                {productResults.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => addLine(p)}
                    className="flex w-full items-center justify-between px-3 py-2 text-left text-body hover:bg-bg-subtle"
                  >
                    <div>
                      <div className="text-fg-primary">{p.name}</div>
                      <div className="text-label text-fg-tertiary">{p.sku || '—'}</div>
                    </div>
                    <div className="text-label text-fg-tertiary">
                      Last: {formatCurrency(p.purchasePrice)} • Stock: {p.currentStock}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Lines table */}
          <div className="flex-1 overflow-auto rounded-md border border-stroke-hairline/10 bg-bg-surface">
            <table className="w-full text-body">
              <thead className="sticky top-0 bg-bg-surface">
                <tr className="border-b border-stroke-hairline/10 text-label uppercase text-fg-tertiary">
                  <th className="px-3 py-2 text-left">Product</th>
                  <th className="px-3 py-2 text-right w-24">Qty</th>
                  <th className="px-3 py-2 text-right w-32">Unit price</th>
                  <th className="px-3 py-2 text-right w-20">Tax %</th>
                  <th className="px-3 py-2 text-right w-32">Total</th>
                  <th className="px-3 py-2 w-10" />
                </tr>
              </thead>
              <tbody>
                {lines.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-3 py-12 text-center text-body text-fg-tertiary">
                      No items yet. Search above to add a product.
                    </td>
                  </tr>
                ) : (
                  lines.map((l, i) => {
                    const t = lineTotals(l);
                    return (
                      <tr key={`${l.product.id}-${i}`} className="border-b border-stroke-hairline/5 last:border-0">
                        <td className="px-3 py-2">
                          <div className="text-fg-primary">{l.product.name}</div>
                          <div className="text-label text-fg-tertiary">{l.product.sku || '—'}</div>
                        </td>
                        <td className="px-3 py-2">
                          <Input
                            value={l.quantity}
                            onChange={(e) => updateLine(i, { quantity: e.target.value })}
                            inputMode="decimal"
                            className="text-right"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <Input
                            value={l.unitPrice}
                            onChange={(e) => updateLine(i, { unitPrice: e.target.value })}
                            inputMode="decimal"
                            className="text-right"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <Input
                            value={l.taxRate}
                            onChange={(e) => updateLine(i, { taxRate: e.target.value })}
                            inputMode="decimal"
                            className="text-right"
                          />
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-fg-primary">{formatCurrency(t.total)}</td>
                        <td className="px-3 py-2">
                          <button
                            type="button"
                            onClick={() => removeLine(i)}
                            className="rounded p-1 text-fg-tertiary hover:bg-bg-subtle hover:text-danger-ink"
                            aria-label="Remove line"
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

        </div>

        {/* RIGHT: meta + totals */}
        <aside className="flex flex-col gap-4 overflow-auto rounded-md border border-stroke-hairline/10 bg-bg-surface p-4">
          {/* Supplier */}
          <div>
            <div className="mb-1 text-label uppercase text-fg-tertiary">Supplier</div>
            {selectedSupplier ? (
              <div className="rounded-md border border-stroke-hairline/10 bg-bg-canvas p-3">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-fg-primary">{selectedSupplier.name}</div>
                    {selectedSupplier.phone && (
                      <div className="text-label text-fg-tertiary">{selectedSupplier.phone}</div>
                    )}
                  </div>
                  <Button variant="ghost" onClick={() => setSelectedSupplier(null)}>Change</Button>
                </div>
                <div className="mt-2 text-label text-fg-tertiary">
                  Outstanding: <span className="text-fg-secondary tabular-nums">{formatCurrency(selectedSupplier.currentBalance)}</span>
                </div>
              </div>
            ) : (
              <div className="relative">
                <Input
                  placeholder="Search supplier…"
                  value={supplierSearch}
                  onChange={(e) => setSupplierSearch(e.target.value)}
                  autoFocus
                />
                {suppliers.length > 0 && (
                  <div className="mt-1 max-h-60 overflow-auto rounded-md border border-stroke-hairline/20 bg-bg-surface">
                    {suppliers.map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => { setSelectedSupplier(s); setSupplierSearch(''); }}
                        className="block w-full px-3 py-2 text-left text-body hover:bg-bg-subtle"
                      >
                        <div className="text-fg-primary">{s.name}</div>
                        {s.phone && <div className="text-label text-fg-tertiary">{s.phone}</div>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Godown */}
          {godowns.length > 0 && (
            <label className="block">
              <span className="mb-1 block text-label uppercase text-fg-tertiary">Receive into</span>
              <select
                className="w-full rounded-md border border-stroke-hairline/20 bg-bg-canvas px-3 py-2 text-body text-fg-primary"
                value={godownId}
                onChange={(e) => setGodownId(e.target.value)}
              >
                {godowns.map((g) => (
                  <option key={g.id} value={g.id}>{g.name}</option>
                ))}
              </select>
            </label>
          )}

          <label className="block">
            <span className="mb-1 block text-label uppercase text-fg-tertiary">Supplier invoice no.</span>
            <Input value={supplierInvoiceNo} onChange={(e) => setSupplierInvoiceNo(e.target.value)} placeholder="Optional" />
          </label>

          <label className="block">
            <span className="mb-1 block text-label uppercase text-fg-tertiary">Invoice date</span>
            <Input type="date" value={supplierInvoiceDate} onChange={(e) => setSupplierInvoiceDate(e.target.value)} />
          </label>

          <label className="block">
            <span className="mb-1 block text-label uppercase text-fg-tertiary">Notes</span>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional remarks" />
          </label>

          <div className="mt-auto rounded-md border border-stroke-hairline/10 bg-bg-canvas p-3">
            <Row label="Subtotal" value={formatCurrency(totals.sub)} />
            <Row label="Tax" value={formatCurrency(totals.tax)} />
            <div className="mt-2 border-t border-stroke-hairline/10 pt-2">
              <Row label="Grand total" value={formatCurrency(totals.grand)} bold />
            </div>
          </div>

          <Button onClick={handleSubmit} disabled={saving || lines.length === 0 || !selectedSupplier} className="w-full">
            <Plus size={14} /> {saving ? 'Saving…' : 'Save purchase'}
          </Button>
        </aside>
      </div>
    </div>
  );
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className={`flex items-baseline justify-between ${bold ? 'text-fg-primary' : 'text-fg-secondary'}`}>
      <span className="text-body">{label}</span>
      <span className={`tabular-nums ${bold ? 'text-heading' : 'text-body'}`}>{value}</span>
    </div>
  );
}
